import React, { useState } from 'react'
import { useRecording } from '../hooks/useRecording'

interface RecordingPanelProps {
  onRecordingComplete: (blob: Blob, label: string) => void
}

const RecordingPanel: React.FC<RecordingPanelProps> = ({ onRecordingComplete }) => {
  const {
    recordingState,
    hasPermission,
    requestPermission,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    clearError,
    formatDuration
  } = useRecording()

  const [showLabelPrompt, setShowLabelPrompt] = useState(false)
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null)
  const [label, setLabel] = useState('')

  const handleStartRecording = async () => {
    try {
      if (hasPermission === false) {
        const granted = await requestPermission()
        if (!granted) return
      }

      await startRecording()
    } catch (error) {
      console.error('Failed to start recording:', error)
    }
  }

  const handleStopRecording = () => {
    const blob = stopRecording()
    if (blob) {
      setRecordingBlob(blob)
      setShowLabelPrompt(true)
    }
  }

  const handleSaveRecording = () => {
    if (recordingBlob && label.trim()) {
      onRecordingComplete(recordingBlob, label.trim())
      setShowLabelPrompt(false)
      setRecordingBlob(null)
      setLabel('')
    }
  }

  const handleCancelRecording = () => {
    setShowLabelPrompt(false)
    setRecordingBlob(null)
    setLabel('')
  }

  const handlePauseResume = () => {
    if (recordingState.isPaused) {
      resumeRecording()
    } else {
      pauseRecording()
    }
  }

  if (showLabelPrompt) {
    return (
      <div className="recording-panel">
        <div className="recording-label-prompt">
          <h3>Save Your Recording</h3>
          <p>Give your sound a name:</p>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Enter sound name..."
            className="recording-label-input"
            autoFocus
            maxLength={50}
          />
          <div className="recording-label-actions">
            <button
              onClick={handleCancelRecording}
              className="recording-cancel-button"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveRecording}
              disabled={!label.trim()}
              className="recording-save-button"
            >
              Save Sound
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="recording-panel">
      <div className="recording-controls">
        <h3>Record New Sound</h3>

        {recordingState.error && (
          <div className="recording-error">
            <p>{recordingState.error}</p>
            <button onClick={clearError} className="recording-error-dismiss">
              Dismiss
            </button>
          </div>
        )}

        {hasPermission === false && (
          <div className="recording-permission">
            <p>Microphone access is required to record sounds.</p>
            <button onClick={requestPermission} className="recording-permission-button">
              Allow Microphone Access
            </button>
          </div>
        )}

        {hasPermission === true && (
          <div className="recording-actions">
            {!recordingState.isRecording ? (
              <button
                onClick={handleStartRecording}
                className="recording-start-button"
              >
                🎤 Start Recording
              </button>
            ) : (
              <div className="recording-active">
                <div className="recording-status">
                  <div className="recording-indicator">
                    <div className="recording-pulse"></div>
                    <span>Recording...</span>
                  </div>
                  <div className="recording-duration">
                    {formatDuration(recordingState.duration)}
                  </div>
                </div>

                <div className="recording-controls-active">
                  <button
                    onClick={handlePauseResume}
                    className="recording-pause-button"
                  >
                    {recordingState.isPaused ? '▶️ Resume' : '⏸️ Pause'}
                  </button>
                  <button
                    onClick={handleStopRecording}
                    className="recording-stop-button"
                  >
                    ⏹️ Stop & Save
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default RecordingPanel
